import { Link } from 'react-router-dom';
import { useEffect, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, Building2, X, Edit, AlertTriangle } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useFournisseurs, useCreateFournisseur, useUpdateFournisseur, useDeleteFournisseur,
} from '@/hooks/useBetonSprint5';
import {
  CATEGORIE_FOURNISSEUR_LABELS, type CategorieFournisseur, type Fournisseur,
} from '@/types';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

export const FournisseursPage = () => {
  const [categorie, setCategorie] = useState<CategorieFournisseur | ''>('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editFor, setEditFor] = useState<Fournisseur | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useFournisseurs({
    categorie: categorie || undefined,
    actif: showInactive ? undefined : true,
    limit: 200,
  });
  const remove = useDeleteFournisseur();
  const four = data?.data ?? [];

  const handle = async (fn: () => Promise<any>, msg?: string) => {
    if (msg && !(await demander.confirmer({ titre: msg, danger: /supprim|annul|rejet|refus|désactiv|écart/i.test(msg) }))) return;
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Fournisseurs"
        subtitle="Module 11 — Référentiel achats (cimenteries · carrières · sidérurgistes · adjuvants)"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => { setEditFor(null); setDialogOpen(true); }}>
              <Plus size={14} /> Nouveau fournisseur
            </button>
          </>
        }
      />
      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-filters">
          <select className="ds-fi" value={categorie} onChange={(e) => setCategorie(e.target.value as CategorieFournisseur | '')} style={{ width: 'auto' }}>
            <option value="">Toutes catégories</option>
            {(Object.keys(CATEGORIE_FOURNISSEUR_LABELS) as CategorieFournisseur[]).map((c) => (
              <option key={c} value={c}>{CATEGORIE_FOURNISSEUR_LABELS[c]}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 px-2 text-xs text-gray-600">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Inclure inactifs
          </label>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Fournisseurs</div>
              <div className="ds-table-sub">{four.length} fournisseur{four.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Code</th>
                <th>Raison sociale</th>
                <th style={{ width: 110 }}>Catégorie</th>
                <th style={{ width: 130 }}>Contact</th>
                <th style={{ width: 110 }}>Ville</th>
                <th style={{ width: 80 }}>Délai pmt</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
              {!isLoading && four.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48 }}>
                  <Building2 size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun fournisseur</p>
                </td></tr>
              )}
              {!isLoading && four.map((f) => (
                <tr key={f.id} style={{ opacity: f.actif ? 1 : 0.5 }}>
                  <td><span className="ds-ref-code">{f.code}</span></td>
                  <td>
                    <div className="ds-entry-label">{f.raisonSociale}</div>
                    {(f as { contactId?: string | null }).contactId && (
                      <Link to={`/finance/releves/${(f as { contactId?: string | null }).contactId}`} style={{ fontSize: 10, color: 'var(--p)' }}>Relevé de compte</Link>
                    )}
                    {f.nif && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>NIF {f.nif}</div>}
                  </td>
                  <td><span className="ds-bd ds-bd-i" style={{ fontSize: 10 }}>{CATEGORIE_FOURNISSEUR_LABELS[f.categorie]}</span></td>
                  <td style={{ fontSize: 11 }}>
                    {f.contactNom && <div>{f.contactNom}</div>}
                    {f.tel && <div style={{ color: 'var(--tx-3)' }}>{f.tel}</div>}
                  </td>
                  <td style={{ fontSize: 11 }}>{f.ville ?? '—'}</td>
                  <td style={{ fontSize: 11 }}>{f.delaiPaiementJours}j</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => { setEditFor(f); setDialogOpen(true); }} title="Modifier">
                        <Edit size={11} />
                      </button>
                      <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => handle(() => remove.mutateAsync(f.id), `Supprimer ${f.code} ?`)} style={{ color: 'var(--danger)' }} title="Supprimer">
                        <X size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FournisseurDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editFor={editFor} />
    </>
  );
};

const FournisseurDialog = ({ open, onClose, editFor }: { open: boolean; onClose: () => void; editFor: Fournisseur | null }) => {
  const [raisonSociale, setRS] = useState('');
  const [categorie, setCat]    = useState<CategorieFournisseur>('cimenterie');
  const [contactNom, setCN]    = useState('');
  const [email, setEmail]      = useState('');
  const [tel, setTel]          = useState('');
  const [adresse, setAdr]      = useState('');
  const [ville, setVille]      = useState('');
  const [pays, setPays]        = useState('Côte d\'Ivoire');
  const [nif, setNif]          = useState('');
  const [delaiPmt, setDP]      = useState('30');
  const [notes, setNotes]      = useState('');
  const [error, setError]      = useState<string | null>(null);

  const create = useCreateFournisseur();
  const update = useUpdateFournisseur();

  useEffect(() => {
    if (open) {
      if (editFor) {
        setRS(editFor.raisonSociale); setCat(editFor.categorie); setCN(editFor.contactNom ?? '');
        setEmail(editFor.email ?? ''); setTel(editFor.tel ?? ''); setAdr(editFor.adresse ?? '');
        setVille(editFor.ville ?? ''); setPays(editFor.pays ?? 'Côte d\'Ivoire');
        setNif(editFor.nif ?? ''); setDP(String(editFor.delaiPaiementJours));
        setNotes(editFor.notes ?? '');
      } else {
        setRS(''); setCat('cimenterie'); setCN(''); setEmail(''); setTel(''); setAdr('');
        setVille(''); setPays('Côte d\'Ivoire'); setNif(''); setDP('30'); setNotes('');
      }
      setError(null);
    }
  }, [open, editFor]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = {
      raisonSociale: raisonSociale.trim(), categorie,
      contactNom: contactNom.trim() || undefined, email: email.trim() || undefined,
      tel: tel.trim() || undefined, adresse: adresse.trim() || undefined,
      ville: ville.trim() || undefined, pays: pays.trim() || undefined,
      nif: nif.trim() || undefined,
      delaiPaiementJours: parseInt(delaiPmt, 10) || 30,
      notes: notes.trim() || undefined,
    };
    try {
      if (editFor) await update.mutateAsync({ id: editFor.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} title={editFor ? `Fournisseur ${editFor.code}` : 'Nouveau fournisseur'} subtitle={editFor ? 'Modification' : 'Auto-code FOUR-CAT-NNN'} size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Raison sociale *">
            <input value={raisonSociale} onChange={(e) => setRS(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Catégorie *">
            <select value={categorie} onChange={(e) => setCat(e.target.value as CategorieFournisseur)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(CATEGORIE_FOURNISSEUR_LABELS) as CategorieFournisseur[]).map((c) => (
                <option key={c} value={c}>{CATEGORIE_FOURNISSEUR_LABELS[c]}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact"><input value={contactNom} onChange={(e) => setCN(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Tél"><input value={tel} onChange={(e) => setTel(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="+225..." /></Field>
        </div>
        <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        <Field label="Adresse"><input value={adresse} onChange={(e) => setAdr(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Ville"><input value={ville} onChange={(e) => setVille(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Pays"><input value={pays} onChange={(e) => setPays(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="NIF/IFU"><input value={nif} onChange={(e) => setNif(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>
        <Field label="Délai paiement (jours)">
          <input type="number" min="0" value={delaiPmt} onChange={(e) => setDP(e.target.value)} className="ds-fi" style={{ width: 150 }} />
        </Field>
        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!raisonSociale.trim() || create.isPending || update.isPending}>
            {editFor ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
